import { formatDate, formatGender, formatPhoneNumber, getAge } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { EnrollmentCheckDate, EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { InformationIcon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { AdditionalBenefitInfoDialog } from 'components/referrals/dialogs/AdditionalBenefitInfoDialog';
import {
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';

export const TreatmentMemberDetails = ({
    payerId = undefined,
    memberDetails = undefined,
    willBeUnmanagedOn = undefined,
    // eslint-disable-next-line complexity
}) => {
    const { agoraEnhancedEligibility } = useFlags();
    const [isAdditionalBenefitInfoDialogOpen, setIsAdditionalBenefitInfoDialogOpen] =
        useState(false);

    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;
    const showWillBeUnmanagedOn =
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING && !!willBeUnmanagedOn;

    const enrollmentRowContent = (() => {
        if (!agoraEnhancedEligibility || !eligibilityDisplay) {
            return null;
        }
        if (showWillBeUnmanagedOn) {
            return (
                <div className="flex__gap--medium" style={{ display: 'flex', width: '100%' }}>
                    <div style={{ width: '50%' }}>
                        <DetailLabel label="Enrollment Status" />
                        <StatusIndicator
                            message={eligibilityDisplay.label}
                            status={eligibilityDisplay.status}
                            tooltipText={eligibilityDisplay.tooltipText}
                        />
                    </div>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Grace period ends"
                        value={formatDate(willBeUnmanagedOn)}
                    />
                </div>
            );
        }
        return (
            <div style={{ width: '100%' }}>
                <DetailLabel label="Enrollment Status" />
                <StatusIndicator
                    message={eligibilityDisplay.label}
                    status={eligibilityDisplay.status}
                    tooltipText={eligibilityDisplay.tooltipText}
                />
            </div>
        );
    })();

    const enrollmentCheckDateRowContent = (() => {
        if (!agoraEnhancedEligibility) {
            return null;
        }
        return (
            <div className="flex__gap--medium" style={{ width: '100%' }}>
                <DetailLabelValuePair
                    style={{ width: '50%' }}
                    label="Coverage Dates"
                    value={
                        <EnrollmentCoverageDates
                            enrollmentStart={memberDetails?.enrollmentStart}
                            enrollmentEnd={memberDetails?.enrollmentEnd}
                            isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                        />
                    }
                />
                <DetailLabelValuePair
                    style={{ width: '50%' }}
                    label="Enrollment Check Date"
                    value={
                        <EnrollmentCheckDate
                            enrollmentCheckDate={memberDetails?.enrollmentCheckDate}
                        />
                    }
                />
            </div>
        );
    })();

    return (
        <FormSectionWithRows
            sectionTitle="Member Details"
            isLoading={!memberDetails}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Gender"
                        value={formatGender(memberDetails?.gender)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="DOB"
                        value={formatDate(memberDetails?.dateOfBirth)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Age"
                        value={getAge(memberDetails?.dateOfBirth)}
                    />
                    <AddressDetailLabelValue
                        style={{ width: '50%' }}
                        address1={memberDetails?.memberAddress1}
                        address2={memberDetails?.memberAddress2}
                        city={memberDetails?.memberCity}
                        state={memberDetails?.memberState}
                        zip={memberDetails?.memberZip}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelList
                        label="Phone"
                        style={{ width: '50%' }}
                        values={[
                            formatPhoneNumber(memberDetails?.phone),
                            ...(memberDetails?.secondaryPhone
                                ? [
                                      // eslint-disable-next-line react/jsx-key
                                      <div
                                          className="flex__gap--x-small"
                                          style={{ alignItems: 'center' }}
                                      >
                                          <span>
                                              {formatPhoneNumber(memberDetails.secondaryPhone)}
                                          </span>
                                          <InformationIcon tooltipText="Payer-submitted supplementary phone number" />
                                      </div>,
                                  ]
                                : []),
                        ]}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Email"
                        value={
                            memberDetails?.email ? (
                                <MailToHyperlink emailAddress={memberDetails?.email} />
                            ) : undefined
                        }
                    />
                </>
            }
            rowFourContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Member Id"
                        value={memberDetails?.memberId}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%', visibility: 'hidden' }}
                        label="Group Name"
                        value={memberDetails?.benefitGroupName}
                    />
                </>
            }
            rowFiveContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Carrier"
                        value={memberDetails?.carrier}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Account"
                        value={memberDetails?.account}
                    />
                </>
            }
            rowSixContent={
                <div
                    className="flex__gap--medium"
                    style={{ width: '100%', flexDirection: 'column', display: 'flex' }}
                >
                    <div className="flex__gap--medium" style={{ display: 'flex', width: '100%' }}>
                        <AdditionalBenefitInfoDialog
                            payerId={payerId}
                            benefitGroup={memberDetails?.benefitGroup}
                            isOpen={isAdditionalBenefitInfoDialogOpen}
                            onClose={() => setIsAdditionalBenefitInfoDialogOpen(false)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Group Id"
                            value={
                                memberDetails?.benefitGroup ? (
                                    <ActionButtonText
                                        label={memberDetails.benefitGroup}
                                        onClick={() => setIsAdditionalBenefitInfoDialogOpen(true)}
                                    />
                                ) : undefined
                            }
                        />
                    </div>
                    {enrollmentRowContent}
                    {enrollmentCheckDateRowContent}
                </div>
            }
        />
    );
};

TreatmentMemberDetails.propTypes = {
    payerId: PropTypes.string,
    willBeUnmanagedOn: PropTypes.string,
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        gender: PropTypes.string,
        dateOfBirth: PropTypes.string,
        memberAddress1: PropTypes.string,
        memberAddress2: PropTypes.string,
        memberCity: PropTypes.string,
        memberState: PropTypes.string,
        memberZip: PropTypes.string,
        benefitGroup: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        enrollmentStatus: PropTypes.string,
        lastUpdated: PropTypes.string,
    }),
};
